/**
 * SEO/GEO-optimised marketplace landing page.
 *
 * One route handler powers six distinct marketplace URLs:
 *   /ai-catalog-myntra
 *   /ai-catalog-ajio
 *   /ai-catalog-nykaa
 *   /ai-catalog-amazon
 *   /ai-catalog-flipkart
 *   /ai-catalog-meesho
 *
 * Each renders unique copy + per-marketplace FAQPage + SoftwareApplication
 * + BreadcrumbList JSON-LD so Google, ChatGPT, Gemini, Claude and
 * Perplexity index AI-catalog queries directly to this page.
 */
import React, { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
  Rocket,
  Sparkles,
  ImageIcon,
  FileSpreadsheet,
  ShieldCheck,
  Zap,
  Brain,
  CheckCircle2,
} from "lucide-react";

const MARKETPLACES = {
  myntra: {
    slug: "myntra",
    brand: "Myntra",
    h1: "AI Catalog for Myntra — Auto Listings, AI Descriptions & Bulk Upload",
    metaTitle:
      "AI Catalog for Myntra | Myntra AI Listing Software & Bulk Upload Automation",
    metaDesc:
      "AI catalog software for Myntra sellers. Auto-fill 225+ Myntra Excel templates, generate AI product descriptions, bulk upload listings, manage inventory and automate Myntra seller panel — all in 90 seconds.",
    intro:
      "EcomCatalog AI is the fastest AI catalog for Myntra. Upload one image and our Claude Vision + Gemini Nano Banana stack auto-fills the entire Myntra Excel template — brand, fabric, neck, sleeve, pattern, HSN, SEO title and product description — ready for Myntra Partner Portal upload in 90 seconds. No more hiring agencies for Myntra catalog uploads.",
    keywords: [
      "AI catalog for Myntra",
      "Myntra AI listing software",
      "Myntra bulk upload automation",
      "AI Myntra seller tools",
      "AI fashion catalog for Myntra",
      "Myntra catalog management AI",
      "AI descriptions for Myntra",
      "Myntra product upload automation",
      "Myntra inventory sync software",
      "Myntra seller automation platform",
    ],
    bullets: [
      "225+ Myntra category templates auto-detected from product image",
      "HSN code, fabric, neck, sleeve, pattern & SEO title auto-filled by AI",
      "Excel generated in <5 seconds — same format as Myntra's official template",
      "All dropdowns (Brand, Colour, Fabric, Size, etc.) preserved & working",
      "Bulk upload 500 SKUs in one Myntra Excel — ready for Partner Portal",
      "Myntra fashion + beauty + footwear + accessories all supported",
    ],
    faqs: [
      {
        q: "How does AI catalog software work for Myntra?",
        a: "Upload a single product photo and the AI (Claude Vision + Gemini Nano Banana) detects category, colour, fabric, neckline, sleeve length, pattern and writes SEO-ready titles and descriptions. The entire Myntra Excel — including HSN code, dropdowns and mandatory yellow columns — is auto-filled. You download the file and upload it directly to Myntra Partner Portal.",
      },
      {
        q: "Can AI automate Myntra product listings?",
        a: "Yes. EcomCatalog AI replaces manual Myntra catalog work and listing agencies. The AI does category detection, attribute extraction, SEO title generation, product description writing, HSN code resolution and bulk Excel generation in under 90 seconds per SKU.",
      },
      {
        q: "Which Myntra categories are supported?",
        a: "225+ categories across Myntra fashion (Tshirts, Kurtis, Sarees, Dresses, Jeans, Shirts, Tops, Lehenga, Innerwear), Myntra footwear (Heels, Flats, Sneakers, Boots), Myntra beauty (Lipstick, Foundation, Eyeliner, Perfume, Nail Polish) and Myntra accessories (Watches, Bags, Belts, Jewellery).",
      },
      {
        q: "Does the AI keep Myntra's mandatory yellow columns filled?",
        a: "Yes — every yellow (mandatory) column on the Myntra Excel is auto-filled with valid dropdown values from Myntra's masterdata: AgeGroup, Country of Origin, Year, Season, Sustainable, Assorted, Pattern, Fabric, Wash Care, HSN code and more.",
      },
      {
        q: "Is the downloaded file Myntra Partner Portal ready?",
        a: "Yes. We keep the exact Myntra template structure including the __INSTRUCTIONS sheet, the masterdata sheet and all data validations (dropdowns). The file uploads directly to Myntra Partner Portal without any manual edits.",
      },
    ],
  },
  ajio: {
    slug: "ajio",
    brand: "AJIO",
    h1: "AI Catalog for AJIO — Bulk Listing Automation for AJIO Sellers",
    metaTitle:
      "AI Catalog for AJIO | AJIO Listing Automation & AI Seller Software",
    metaDesc:
      "AI catalog software for AJIO sellers. Auto-generate AJIO product listings, bulk upload SKUs, AI fashion descriptions and inventory sync — all in 90 seconds with AI.",
    intro:
      "AJIO sellers waste hours on manual catalog work. EcomCatalog AI is the fastest AI catalog for AJIO — one image in, full AJIO listing out. Auto category detection, AI descriptions, SEO titles and bulk Excel generation make AJIO catalog management 10× faster than hiring an agency.",
    keywords: [
      "AI catalog for AJIO",
      "AJIO listing automation",
      "AJIO AI seller software",
      "AJIO catalog upload automation",
      "AI marketplace integration AJIO",
      "AJIO bulk listing AI",
      "AJIO fashion product automation",
      "AJIO seller panel AI",
      "AI listing generator for AJIO",
      "AJIO inventory automation",
    ],
    bullets: [
      "AI auto-fill for AJIO fashion + footwear categories",
      "SEO-optimised AJIO product titles and descriptions",
      "Bulk listing — 500 SKUs in one Excel for AJIO",
      "AI fashion metadata: fabric, fit, occasion, season",
      "Image-driven AJIO catalog — no manual typing",
      "Inventory sync friendly: clean SKU master across marketplaces",
    ],
    faqs: [
      {
        q: "How does AI catalog software work for AJIO?",
        a: "Drop a product photo into EcomCatalog AI. The AI detects category, colour, fabric, fit, pattern and writes AJIO-ready SEO descriptions. You download a ready-to-upload AJIO catalog file and push it to your AJIO seller panel.",
      },
      {
        q: "Can AI write AJIO product descriptions?",
        a: "Yes. Our Claude Vision model writes high-intent, keyword-rich product descriptions optimised for AJIO's search algorithm and for buyer conversion.",
      },
      {
        q: "Does it support bulk AJIO uploads?",
        a: "Yes. Save multiple products in a single session and download one consolidated Excel — up to 500 SKUs at a time.",
      },
    ],
  },
  nykaa: {
    slug: "nykaa",
    brand: "Nykaa",
    h1: "AI Catalog for Nykaa — Beauty & Cosmetics Listing Automation",
    metaTitle:
      "AI Catalog for Nykaa | Nykaa Beauty Listing Automation Software",
    metaDesc:
      "AI catalog software for Nykaa beauty sellers. AI-generated cosmetic descriptions, bulk product upload, ingredient detection, AI listings for Nykaa Fashion & Nykaa beauty in 90 seconds.",
    intro:
      "EcomCatalog AI is the only AI catalog for Nykaa that understands beauty SKUs natively — no garment-style size compulsion, ingredient-aware descriptions and Nykaa-specific template support. Beauty, fragrance and cosmetic listings ready in under 90 seconds.",
    keywords: [
      "AI catalog for Nykaa",
      "Nykaa listing automation",
      "AI beauty catalog software",
      "Nykaa AI seller tools",
      "AI cosmetic product listing",
      "Nykaa inventory sync AI",
      "Nykaa bulk upload automation",
      "AI-generated beauty descriptions",
      "Nykaa marketplace automation",
      "AI listing tool for Nykaa",
    ],
    bullets: [
      "Beauty-aware AI — Lipstick, Foundation, Mascara, Perfume, Nail Polish",
      "No size compulsion — single SKU per beauty product",
      "Ingredient-friendly AI descriptions and benefit-driven copy",
      "Nykaa & Nykaa Fashion catalog templates supported",
      "Bulk beauty SKU upload with one click",
      "Auto HSN for cosmetics (3304, etc.)",
    ],
    faqs: [
      {
        q: "How does AI catalog software work for Nykaa beauty sellers?",
        a: "EcomCatalog AI detects beauty product type (Lipstick, Foundation, Eyeliner, Perfume) from the image, writes Nykaa-ready descriptions, fills HSN codes and generates a Nykaa-compatible Excel — all without manual data entry.",
      },
      {
        q: "Can AI write beauty product descriptions?",
        a: "Yes. The AI writes ingredient-aware, benefit-driven, SEO-rich product descriptions optimised for beauty buyers on Nykaa.",
      },
      {
        q: "Are Nykaa-specific categories supported?",
        a: "Yes — Lipstick, Foundation, Concealer, Mascara, Eyeliner, Perfume, Nail Polish, Nail Polish Remover, Nail Art, Hair Care, Skincare and Body Care templates are all built-in.",
      },
    ],
  },
  amazon: {
    slug: "amazon",
    brand: "Amazon",
    h1: "AI Catalog for Amazon — AI Listing Automation for Amazon Sellers India",
    metaTitle:
      "AI Catalog for Amazon | AI Listing Software for Amazon India Sellers",
    metaDesc:
      "AI catalog software for Amazon India sellers. Auto-generate Amazon listings, bullet points, A+ content, bulk upload, AI product descriptions for Amazon flat-file template.",
    intro:
      "EcomCatalog AI accelerates Amazon catalog creation. Upload an image, get AI-generated title, bullet points, search terms, backend keywords and product description. Designed for Amazon India sellers who want to bulk-list 500+ SKUs without the cost of hiring an Amazon catalog agency.",
    keywords: [
      "AI catalog for Amazon",
      "Amazon listing automation",
      "AI Amazon seller software India",
      "AI listing generator for Amazon",
      "Amazon bulk listing AI",
      "AI Amazon flat-file generator",
      "AI product description for Amazon",
      "Amazon search-term optimiser AI",
    ],
    bullets: [
      "AI-optimised Amazon title within 200-character limit",
      "5 bullet points generated per SKU",
      "Backend search terms + keyword research",
      "Bulk SKU export for Amazon flat-file",
      "Multi-marketplace SKU master included",
    ],
    faqs: [
      {
        q: "How does AI catalog software work for Amazon sellers?",
        a: "Drop a product photo into EcomCatalog AI and the AI writes an Amazon-ready title (≤200 chars), 5 SEO bullet points, backend search terms and a 200-word description — fully formatted for Amazon flat-file upload.",
      },
      {
        q: "Can AI generate Amazon bullet points?",
        a: "Yes. The AI writes 5 high-conversion bullet points per product, optimised for Amazon India search and buyer intent.",
      },
    ],
  },
  flipkart: {
    slug: "flipkart",
    brand: "Flipkart",
    h1: "AI Catalog for Flipkart — AI Listing & Bulk Upload for Flipkart Sellers",
    metaTitle:
      "AI Catalog for Flipkart | AI Listing Software for Flipkart India Sellers",
    metaDesc:
      "AI catalog software for Flipkart sellers. Auto-generate Flipkart product listings, bulk upload templates, AI descriptions and inventory sync — designed for Indian D2C brands.",
    intro:
      "EcomCatalog AI is the AI catalog tool for Flipkart sellers who want to skip agencies. One image → full Flipkart listing with title, description, attributes, HSN and category mapping in under 90 seconds.",
    keywords: [
      "AI catalog for Flipkart",
      "Flipkart listing automation",
      "AI Flipkart seller software",
      "Flipkart bulk listing AI",
      "AI Flipkart product description",
      "Flipkart catalog management",
    ],
    bullets: [
      "AI category mapping for Flipkart taxonomy",
      "Flipkart-ready title, description and attribute fill",
      "Bulk Excel for Flipkart seller hub",
      "HSN auto-detection per Flipkart category",
    ],
    faqs: [
      {
        q: "How does AI catalog software work for Flipkart sellers?",
        a: "EcomCatalog AI auto-detects Flipkart category, writes SEO-ready titles and descriptions, fills attributes and HSN — and generates a bulk Excel ready for Flipkart Seller Hub upload.",
      },
    ],
  },
  meesho: {
    slug: "meesho",
    brand: "Meesho",
    h1: "AI Catalog for Meesho — Bulk Listing & AI Descriptions for Meesho Sellers",
    metaTitle:
      "AI Catalog for Meesho | AI Listing Automation for Meesho Suppliers",
    metaDesc:
      "AI catalog software for Meesho suppliers. AI-generated product titles, bulk catalog upload, image-based attribute detection and Meesho-ready listings in 90 seconds.",
    intro:
      "EcomCatalog AI is the AI catalog for Meesho suppliers — image-first, agency-free, bulk-ready. Auto category, AI description, attribute extraction and Meesho-compatible Excel in one workflow.",
    keywords: [
      "AI catalog for Meesho",
      "Meesho listing automation",
      "AI Meesho supplier software",
      "Meesho bulk catalog upload",
      "AI Meesho product description",
    ],
    bullets: [
      "AI category + attribute detection for Meesho",
      "Title, description and tags ready for Meesho",
      "Bulk Excel for Meesho Supplier Panel",
      "Image-based — no manual typing",
    ],
    faqs: [
      {
        q: "How does AI catalog software work for Meesho suppliers?",
        a: "EcomCatalog AI lets Meesho suppliers upload an image, auto-detect category and attributes, write AI descriptions and generate Meesho-ready bulk catalog Excel in 90 seconds per SKU.",
      },
    ],
  },
};

const COMMON_FAQS = [
  {
    q: "What is AI catalog automation?",
    a: "AI catalog automation uses computer vision and large language models to read product images and automatically generate marketplace-ready listings — including category, title, description, attributes, HSN code and bulk upload Excel files. It replaces manual catalog work and listing agencies.",
  },
  {
    q: "How does AI listing software work?",
    a: "AI listing software like EcomCatalog AI uses Claude Vision and Gemini Nano Banana to inspect a product photo, detect dozens of attributes (category, fabric, colour, pattern, neck, sleeve, occasion, etc.) and produce a fully filled marketplace Excel — Myntra, AJIO, Nykaa, Amazon, Flipkart or Meesho — in under 90 seconds.",
  },
  {
    q: "Can AI upload products in bulk?",
    a: "Yes. EcomCatalog AI supports bulk product upload of up to 500 SKUs per Excel. Sellers can stack multiple products in one session and export a single consolidated marketplace-ready file.",
  },
  {
    q: "Which marketplaces does this AI catalog software support?",
    a: "EcomCatalog AI supports Myntra, AJIO, Nykaa, Amazon India, Flipkart, Meesho, Swiggy Instamart and Blinkit. New marketplaces are added regularly.",
  },
  {
    q: "Is AI catalog software better than hiring an agency?",
    a: "Yes. AI catalog automation is 10× faster and ~95% cheaper than hiring a manual catalog agency. EcomCatalog AI generates Myntra-quality listings in seconds, with built-in SEO, AI descriptions and bulk Excel export.",
  },
];

function injectJsonLd(id, data) {
  let s = document.getElementById(id);
  if (s) s.remove();
  s = document.createElement("script");
  s.id = id;
  s.type = "application/ld+json";
  s.textContent = JSON.stringify(data);
  document.head.appendChild(s);
}

export default function MarketplaceLanding() {
  // React Router cannot extract a literal-prefix param like `/ai-catalog-myntra`,
  // so we read the slug from the URL path itself.
  const slugFromPath = (typeof window !== "undefined" ? window.location.pathname : "")
    .replace(/^\/ai-catalog-/, "")
    .toLowerCase();
  const { slug } = useParams();
  const finalSlug = (slug || slugFromPath || "").toLowerCase();
  const mp = MARKETPLACES[finalSlug];

  // Apply per-marketplace SEO + JSON-LD on every mount/route change.
  useEffect(() => {
    if (!mp) return;
    const url = `https://ecomcatalog.ai/ai-catalog-${mp.slug}`;

    document.title = mp.metaTitle;
    const setMeta = (sel, content) => {
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        const m = sel.match(/\[(name|property)="([^"]+)"\]/);
        if (m) el.setAttribute(m[1], m[2]);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta('meta[name="description"]', mp.metaDesc);
    setMeta(
      'meta[name="keywords"]',
      [...mp.keywords, "AI catalog software", "AI listing software", "AI ecommerce automation"].join(", ")
    );
    setMeta('meta[property="og:title"]', mp.metaTitle);
    setMeta('meta[property="og:description"]', mp.metaDesc);
    setMeta('meta[property="og:url"]', url);
    setMeta('meta[name="twitter:title"]', mp.metaTitle);
    setMeta('meta[name="twitter:description"]', mp.metaDesc);

    // Canonical
    let can = document.head.querySelector('link[rel="canonical"]');
    if (!can) {
      can = document.createElement("link");
      can.setAttribute("rel", "canonical");
      document.head.appendChild(can);
    }
    can.setAttribute("href", url);

    // Per-page SoftwareApplication JSON-LD
    injectJsonLd(`ld-${mp.slug}-app`, {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: `EcomCatalog AI — AI Catalog for ${mp.brand}`,
      applicationCategory: "BusinessApplication",
      operatingSystem: "Web",
      url,
      description: mp.metaDesc,
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "INR",
        availability: "https://schema.org/InStock",
      },
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: "187",
      },
      featureList: mp.bullets,
    });

    // Per-page FAQPage JSON-LD (marketplace FAQs + common FAQs)
    injectJsonLd(`ld-${mp.slug}-faq`, {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [...mp.faqs, ...COMMON_FAQS].map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    });

    // BreadcrumbList JSON-LD
    injectJsonLd(`ld-${mp.slug}-bc`, {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://ecomcatalog.ai/" },
        { "@type": "ListItem", position: 2, name: "AI Catalog Automation", item: "https://ecomcatalog.ai/features" },
        { "@type": "ListItem", position: 3, name: `AI Catalog for ${mp.brand}`, item: url },
      ],
    });

    return () => {
      // Cleanup so SPA navigation doesn't accumulate stale schema.
      [`ld-${mp.slug}-app`, `ld-${mp.slug}-faq`, `ld-${mp.slug}-bc`].forEach((id) => {
        const el = document.getElementById(id);
        if (el) el.remove();
      });
    };
  }, [mp]);

  if (!mp) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#0B0F1A] text-white" data-testid={`marketplace-${mp.slug}`}>
      {/* HERO */}
      <header className="border-b border-white/[0.06] bg-gradient-to-b from-[#1a1145] to-[#0B0F1A]">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <Link to="/" className="text-lg font-bold tracking-tight">
            EcomCatalog<span className="text-[#4F46E5]">.ai</span>
          </Link>
          <nav className="flex gap-6 text-sm text-white/70">
            <Link to="/features" className="hover:text-white">Features</Link>
            <Link to="/pricing" className="hover:text-white">Pricing</Link>
            <Link to="/blog" className="hover:text-white">Blog</Link>
            <Link to="/login" className="font-bold text-white">Sign in</Link>
          </nav>
        </div>
        <section className="max-w-6xl mx-auto px-6 py-16">
          <p className="text-[#A78BFA] uppercase text-xs tracking-widest font-bold mb-3">
            AI Catalog Automation for {mp.brand}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-tight tracking-tight">
            {mp.h1}
          </h1>
          <p className="mt-6 text-lg text-white/75 max-w-3xl leading-relaxed">
            {mp.intro}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/login"
              className="px-6 py-3 rounded-lg bg-[#4F46E5] hover:bg-[#6366F1] font-bold text-sm transition"
              data-testid={`cta-start-${mp.slug}`}
            >
              <Zap size={14} className="inline mr-2" /> Start free trial
            </Link>
            <Link
              to="/contact"
              className="px-6 py-3 rounded-lg border border-white/15 hover:bg-white/5 font-bold text-sm transition"
              data-testid={`cta-demo-${mp.slug}`}
            >
              Book a demo
            </Link>
          </div>
        </section>
      </header>

      {/* FEATURE BULLETS */}
      <section className="max-w-6xl mx-auto px-6 py-16">
        <h2 className="text-2xl font-black mb-8">
          Why {mp.brand} sellers pick EcomCatalog AI
        </h2>
        <div className="grid sm:grid-cols-2 gap-5">
          {mp.bullets.map((b, i) => (
            <div
              key={i}
              className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-5 flex gap-3"
            >
              <CheckCircle2 size={18} className="text-emerald-400 shrink-0 mt-0.5" />
              <p className="text-sm text-white/85 leading-relaxed">{b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="max-w-6xl mx-auto px-6 py-16 border-t border-white/[0.06]">
        <h2 className="text-2xl font-black mb-8">
          How AI catalog automation works for {mp.brand}
        </h2>
        <div className="grid md:grid-cols-3 gap-5">
          {[
            { icon: ImageIcon, h: "1. Upload one product photo", p: `Drag in any phone shot — front, side, or model wearing. Compressed to 384px and sent to Claude Vision for ${mp.brand}-aware attribute extraction.` },
            { icon: Brain, h: "2. AI auto-fills 25+ attributes", p: "Category, colour, fabric, neck, sleeve, pattern, occasion, HSN code, SEO title and 200-word product description — all auto-filled in 3 seconds." },
            { icon: FileSpreadsheet, h: `3. Download ${mp.brand}-ready Excel`, p: `Bulk Excel with dropdowns intact, mandatory yellow columns filled and the original ${mp.brand} template structure preserved. Upload directly.` },
          ].map((s, i) => (
            <div key={i} className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-6">
              <s.icon size={22} className="text-[#A78BFA] mb-3" />
              <h3 className="font-bold mb-2">{s.h}</h3>
              <p className="text-sm text-white/65 leading-relaxed">{s.p}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ (page-visible + JSON-LD already injected) */}
      <section className="max-w-4xl mx-auto px-6 py-16 border-t border-white/[0.06]">
        <h2 className="text-2xl font-black mb-8">
          {mp.brand} AI Catalog FAQ
        </h2>
        <div className="space-y-4">
          {[...mp.faqs, ...COMMON_FAQS].map((f, i) => (
            <details
              key={i}
              className="bg-white/[0.03] border border-white/[0.08] rounded-xl p-5"
            >
              <summary className="font-bold cursor-pointer text-white/90">
                {f.q}
              </summary>
              <p className="mt-3 text-sm text-white/70 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Cross-link to other marketplaces (internal linking → SEO) */}
      <section className="max-w-6xl mx-auto px-6 py-12 border-t border-white/[0.06]">
        <h2 className="text-lg font-black mb-4 text-white/85">
          AI Catalog for other marketplaces
        </h2>
        <div className="flex flex-wrap gap-2">
          {Object.values(MARKETPLACES)
            .filter((m) => m.slug !== mp.slug)
            .map((m) => (
              <Link
                key={m.slug}
                to={`/ai-catalog-${m.slug}`}
                className="px-3 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08] text-xs text-white/75 hover:bg-white/[0.08] hover:text-white transition"
                data-testid={`crosslink-${m.slug}`}
              >
                AI Catalog for {m.brand}
              </Link>
            ))}
        </div>
      </section>

      {/* CTA FOOTER */}
      <footer className="border-t border-white/[0.06] bg-white/[0.02]">
        <div className="max-w-6xl mx-auto px-6 py-12 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="font-black text-lg">
              Ready to automate your {mp.brand} catalog?
            </p>
            <p className="text-sm text-white/60 mt-1">
              No credit card. 50 free AI listings every month.
            </p>
          </div>
          <Link
            to="/login"
            className="px-6 py-3 rounded-lg bg-[#4F46E5] hover:bg-[#6366F1] font-bold text-sm transition"
            data-testid={`footer-cta-${mp.slug}`}
          >
            <Rocket size={14} className="inline mr-2" /> Get started free
          </Link>
        </div>
        <div className="text-center text-xs text-white/40 pb-6">
          © EcomCatalog AI · AI Catalog & Listing Automation Platform for
          Myntra, AJIO, Nykaa, Amazon, Flipkart, Meesho ·
          <Link to="/about" className="hover:text-white mx-1">About</Link>·
          <Link to="/contact" className="hover:text-white mx-1">Contact</Link>·
          <Link to="/blog" className="hover:text-white mx-1">Blog</Link>
        </div>
      </footer>
    </div>
  );
}
